import React, {

  useCallback,

  useEffect,

  useMemo,

  useRef,

  useState,

} from 'react';



import clinicalNoteService from './clinicalNoteService.js';
import './ClinicalNoteStrengthCompact.css';



/**

 * Delay between the clinician's last edit and autosave.

 */

const AUTOSAVE_DELAY_MS = 900;



/**

 * Resolve ClinicalNoteMain.entityVersion from a note response.

 */

function resolveEntityVersion(response) {

  if (!response) {

    return null;

  }



  return (

    response.entityVersion ??

    response.note?.entityVersion ??

    response.clinicalNote?.entityVersion ??

    null

  );

}



/**

 * Stable frontend key for one strength-template/side combination.

 *

 * Newly configured fields intentionally have no database ID until

 * their first save, so clinicalNoteStrengthId cannot be used as the

 * React key.

 */

function getStrengthKey(row) {

  return (

    `${row?.strengthTemplateId ?? 'unknown'}|` +

    `${row?.measurementSide ?? 'NONE'}`

  );

}



/**

 * Convert backend laterality into a readable label.

 */

function formatSide(side) {

  switch (side) {

    case 'LEFT':

      return 'Left';



    case 'RIGHT':

      return 'Right';



    case 'BILATERAL':

      return 'Bilateral';



    case 'NOT_APPLICABLE':

      return '';



    default:

      return side || '';

  }

}



/**

 * Normalize one StrengthResponse for controlled React inputs.

 */

function normalizeStrengthRow(row) {

  return {

    ...row,



    draftStrengthGrade:

      row?.strengthGrade ?? '',



    draftComments:

      row?.comments ?? '',

  };

}



/**

 * Sort strength fields by backend display order, then name and side.

 */

function sortStrengthRows(rows) {

  return [...rows].sort(

    (first, second) => {

      const firstOrder =

        Number.isFinite(

          Number(

            first?.displayOrder

          )

        )

          ? Number(

              first.displayOrder

            )

          : 0;



      const secondOrder =

        Number.isFinite(

          Number(

            second?.displayOrder

          )

        )

          ? Number(

              second.displayOrder

            )

          : 0;



      if (

        firstOrder !==

        secondOrder

      ) {

        return (

          firstOrder -

          secondOrder

        );

      }



      const nameComparison =

        String(

          first?.displayName ??

            ''

        ).localeCompare(

          String(

            second?.displayName ??

              ''

          )

        );



      if (

        nameComparison !== 0

      ) {

        return nameComparison;

      }



      return String(

        first?.measurementSide ??

          ''

      ).localeCompare(

        String(

          second?.measurementSide ??

            ''

        )

      );

    }

  );

}



/**

 * Find one backend StrengthGrade metadata option.

 */

function findGradeOption(

  gradeOptions,

  value

) {

  if (

    value === null ||

    value === undefined ||

    value === ''

  ) {

    return null;

  }



  return (

    gradeOptions.find(

      (option) =>

        option?.value === value

    ) ?? null

  );

}



/**

 * Resolve a clinician-facing strength-grade label.

 */

function getGradeDisplay(

  gradeOptions,

  value,

  fallback

) {

  if (

    fallback !== null &&

    fallback !== undefined &&

    String(fallback).trim() !== ''

  ) {

    return String(fallback);

  }



  const option =

    findGradeOption(

      gradeOptions,

      value

    );



  return (

    option?.displayValue ??

    value ??

    '—'

  );

}



/**

 * Validate one current draft.

 *

 * Blank required values remain allowed during DRAFT editing.

 * Complete note validation will block review if required data remains

 * missing.

 */

function validateStrengthRow(

  row,

  gradeOptions

) {

  const errors = {};



  const value =

    row?.draftStrengthGrade ??

    '';



  if (!value) {

    return errors;

  }



  const option =

    findGradeOption(

      gradeOptions,

      value

    );



  if (!option) {

    errors.strengthGrade =

      'The selected strength grade is not recognized by the server.';

  }



  return errors;

}



/**

 * Calculate the live ordered-step change using the order returned by

 * the backend metadata endpoint.

 *

 * The backend remains authoritative and performs its own

 * StrengthGrade.stepsTo(...) calculation when saving.

 */

function calculateLiveStepChange(

  row,

  gradeOptions

) {

  const currentValue =

    row?.draftStrengthGrade;



  const previousValue =

    row?.previousStrengthGrade;



  if (

    !currentValue ||

    !previousValue

  ) {

    return null;

  }



  const current =

    findGradeOption(

      gradeOptions,

      currentValue

    );



  const previous =

    findGradeOption(

      gradeOptions,

      previousValue

    );



  if (

    !current ||

    !previous

  ) {

    return null;

  }



  const currentOrder =

    Number(current.order);



  const previousOrder =

    Number(previous.order);



  if (

    !Number.isFinite(

      currentOrder

    ) ||

    !Number.isFinite(

      previousOrder

    )

  ) {

    return null;

  }



  return (

    currentOrder -

    previousOrder

  );

}



/**

 * Determine whether the current unsaved selection appears likely to

 * exceed the configured maximum progression.

 *

 * This is only an immediate visual heads-up.

 *

 * The authoritative result is calculated by Spring Boot after save.

 */

function predictsIncreaseWarning(

  row,

  gradeOptions

) {

  const change =

    calculateLiveStepChange(

      row,

      gradeOptions

    );



  const maximum =

    row?.maxDailyIncreaseSteps;



  if (

    change === null ||

    maximum === null ||

    maximum === undefined

  ) {

    return false;

  }



  const limit =

    Number(maximum);



  if (

    !Number.isFinite(limit) ||

    limit < 0

  ) {

    return false;

  }



  return change > limit;

}



/**

 * Human-readable step change.

 */

function formatStepChange(value) {

  if (

    value === null ||

    value === undefined

  ) {

    return '—';

  }



  const numeric =

    Number(value);



  if (

    !Number.isFinite(

      numeric

    )

  ) {

    return '—';

  }



  if (numeric === 0) {

    return 'No change';

  }



  const sign =

    numeric > 0

      ? '+'

      : '';



  const absolute =

    Math.abs(numeric);



  return (

    `${sign}${numeric} ` +

    `${absolute === 1 ? 'step' : 'steps'}`

  );

}



/**

 * Merge one successful StrengthResponse back into its editor row.

 *

 * If the clinician made another edit while the request was in flight,

 * retain the newest local draft while refreshing the server metadata.

 */

function mergeSavedStrengthRow(

  currentRow,

  savedResponse,

  preserveDraft

) {

  const normalized =

    normalizeStrengthRow(

      savedResponse

    );



  if (!preserveDraft) {

    return normalized;

  }



  return {

    ...normalized,



    draftStrengthGrade:

      currentRow

        .draftStrengthGrade,



    draftComments:

      currentRow

        .draftComments,

  };

}



/**

 * One Gross Muscle Manual Test measurement row.

 */

function StrengthMeasurementRow({
  row, gradeOptions, disabled, status, saveError, validationError,
  onGradeChange, onCommentsChange, onRetry,
}) {
  const [showComparison, setShowComparison] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showWarnings, setShowWarnings] = useState(false);
  const side = formatSide(row.measurementSide);
  const change = calculateLiveStepChange(row, gradeOptions);
  const predicted = predictsIncreaseWarning(row, gradeOptions);
  const increaseWarning = Boolean(row.increaseWarning);
  const repetitionWarning = Boolean(row.repetitionWarning);
  const unresolved = (increaseWarning || repetitionWarning) && !row.warningAcknowledged;
  const hasValue = Boolean(row.draftStrengthGrade) && !validationError;
  const needsDefaultReview = hasValue && row.clinicalNoteStrengthId == null;
  const state = !row.required ? 'optional' : !hasValue ? 'red' : needsDefaultReview ? 'yellow' : 'green';
  const previous = row.previousStrengthGrade
    ? getGradeDisplay(gradeOptions, row.previousStrengthGrade, row.previousStrengthGradeDisplay)
    : 'No previous grade';
  const showIssue = unresolved || predicted || Boolean(saveError) || Boolean(validationError);
  return (
    <article className={`clinical-strength-row clinical-strength-row--${state}`}>
      <div className="clinical-strength-row__top">
        <div className="clinical-strength-row__name">
          <strong>{row.displayName || row.strengthCode || 'Strength'}</strong>
          {side && <span className="clinical-strength-row__side">{side}</span>}
          {row.required && <span className={`clinical-strength-row__state is-${state}`}>
            {state === 'green' ? 'Complete' : state === 'yellow' ? 'Review default' : 'Required'}
          </span>}
          {unresolved && <span className="clinical-strength-row__warning">Warning</span>}
        </div>
        <span className="clinical-strength-row__save" aria-live="polite">
          {status === 'saving' ? 'Saving…' : status === 'unsaved' ? 'Unsaved' :
           status === 'invalid' ? 'Needs correction' : status === 'error' ? 'Autosave failed' :
           disabled ? 'Read only' : 'Saved'}
        </span>
      </div>
      <div className="clinical-strength-row__main">
        <label htmlFor={`strength-grade-${getStrengthKey(row)}`}>Muscle test grade</label>
        <select id={`strength-grade-${getStrengthKey(row)}`}
          value={row.draftStrengthGrade} disabled={disabled}
          onChange={(event) => onGradeChange(event.target.value)}
          aria-invalid={Boolean(validationError)}>
          <option value="">Select grade</option>
          {gradeOptions.map((option) => <option key={option.value} value={option.value}>
            {option.displayValue}
          </option>)}
        </select>
        <span className="clinical-strength-row__summary" title="Change from previous visit">
          {change === null ? 'No comparison' : formatStepChange(change)}
        </span>
        <button type="button" className="clinical-strength-row__small-button"
          aria-expanded={showComparison} onClick={() => setShowComparison((v) => !v)}>
          {showComparison ? 'Hide previous' : 'Previous / Change'}
        </button>
        <button type="button" className="clinical-strength-row__small-button"
          aria-expanded={showComments} onClick={() => setShowComments((v) => !v)}>
          {showComments ? 'Hide comments' : row.draftComments?.trim() ? 'Comments •' : 'Comments'}
        </button>
      </div>
      {validationError && <div className="clinical-strength-row__error" role="alert">{validationError}</div>}
      {showComparison && <div className="clinical-strength-row__comparison">
        <div><small>Previous grade</small><strong>{previous}</strong></div>
        <div><small>Change</small><strong>{formatStepChange(change)}</strong></div>
        <div><small>Max increase</small><strong>{row.maxDailyIncreaseSteps == null
          ? 'Not configured' : `${row.maxDailyIncreaseSteps} step(s)`}</strong></div>
      </div>}
      {showComments && <div className="clinical-strength-row__comments">
        <label htmlFor={`strength-comments-${getStrengthKey(row)}`}>Optional comments</label>
        <textarea id={`strength-comments-${getStrengthKey(row)}`} rows={3}
          value={row.draftComments} disabled={disabled}
          placeholder="Optional strength comments" onChange={(event) => onCommentsChange(event.target.value)} />
      </div>}
      {showIssue && <div className="clinical-strength-row__issue">
        <button type="button" className="clinical-strength-row__issue-toggle"
          aria-expanded={showWarnings} onClick={() => setShowWarnings((v) => !v)}>
          {saveError ? 'Autosave failed' : unresolved ? 'Strength warning needs attention' :
           predicted ? 'Possible excessive strength increase' : 'Measurement needs correction'}
          {' '}{showWarnings ? '−' : '+'}
        </button>
        {showWarnings && <div className="clinical-strength-row__issue-detail">
          {predicted && !increaseWarning && <p>Possible increase beyond the configured daily limit. The backend decides when saving.</p>}
          {increaseWarning && <p>Strength increased more than the configured number of steps.</p>}
          {repetitionWarning && <p>Strength remained unchanged for too many consecutive visits.</p>}
          {(increaseWarning || repetitionWarning) && <p>{row.warningAcknowledged
            ? `Acknowledged${row.warningReason ? `: ${row.warningReason}` : ''}`
            : 'Unresolved. Review or acknowledge through the clinical warning panel.'}</p>}
          {saveError && <div role="alert"><p>{saveError}</p>
            {!disabled && <button type="button" onClick={onRetry}>Retry Save</button>}</div>}
        </div>}
      </div>}
    </article>
  );
}


/**

 * Strength / Gross Muscle Manual Test editor.

 *

 * Features:

 *

 * - backend-driven strength-grade options

 * - configured strength fields visible before first save

 * - separate LEFT / RIGHT rows for bilateral notes

 * - previous-treatment comparison

 * - live ordered-step comparison

 * - maximum progression heads-up

 * - backend progression/repetition warnings

 * - optional comments

 * - draft null values

 * - serialized autosaving

 * - optimistic-lock version refresh after every successful save

 */

export default function ClinicalNoteStrengthSection({

  clinicalNoteId,

  disabled = false,

  onSaved,

  onVersionChange,
  navigationCoordinator,

}) {

  const [

    rows,

    setRows,

  ] = useState([]);



  const [

    gradeOptions,

    setGradeOptions,

  ] = useState([]);



  const [

    loading,

    setLoading,

  ] = useState(true);



  const [

    loadError,

    setLoadError,

  ] = useState('');



  const [

    rowStatuses,

    setRowStatuses,

  ] = useState({});



  const [

    rowSaveErrors,

    setRowSaveErrors,

  ] = useState({});



  const [

    editTick,

    setEditTick,

  ] = useState(0);



  const [

    initialized,

    setInitialized,

  ] = useState(false);



  /*

   * Autosave coordination refs.

   */

  const rowsRef =

    useRef([]);



  const gradeOptionsRef =

    useRef([]);



  const versionRef =

    useRef(null);



  const rowRevisionRef =

    useRef({});



  const dirtyKeysRef =

    useRef(new Set());



  const failedKeysRef =

    useRef(new Set());



  const savingRef =

    useRef(false);



  const mountedRef =

    useRef(true);



  const flushSaveRef = useRef(null);
  const inFlightSaveRef = useRef(null);
  const unsavedKeysRef = useRef(new Set());
  const initializedRef = useRef(false);
  const loadErrorRef = useRef('');



  useEffect(() => {

    rowsRef.current =

      rows;

  }, [rows]);



  useEffect(() => {

    gradeOptionsRef.current =

      gradeOptions;

  }, [gradeOptions]);



  useEffect(() => {

    return () => {

      mountedRef.current =

        false;

    };

  }, []);



  /**

   * Load:

   *

   * - configured/saved strength measurements

   * - backend StrengthGrade options

   * - current ClinicalNoteMain version

   */

  useEffect(() => {

    let cancelled = false;



    async function loadStrengthSection() {

      setLoading(true);

      setLoadError('');
      loadErrorRef.current = '';
      initializedRef.current = false;
      unsavedKeysRef.current = new Set();

      setInitialized(false);

      setRowStatuses({});

      setRowSaveErrors({});



      dirtyKeysRef.current =

        new Set();



      failedKeysRef.current =

        new Set();



      rowRevisionRef.current =

        {};



      savingRef.current =

        false;



      try {

        const [

          strengthResponse,

          gradeResponse,

          noteResponse,

        ] =

          await Promise.all([

            clinicalNoteService

              .getStrength(

                clinicalNoteId

              ),



            clinicalNoteService

              .getStrengthGrades(),



            clinicalNoteService

              .getClinicalNote(

                clinicalNoteId

              ),

          ]);



        if (cancelled) {

          return;

        }



        const version =

          resolveEntityVersion(

            noteResponse

          );



        if (

          version === null ||

          version === undefined

        ) {

          throw new Error(

            'The clinical note version could not be loaded.'

          );

        }



        if (

          !Array.isArray(

            gradeResponse

          ) ||

          gradeResponse.length ===

            0

        ) {

          throw new Error(

            'No strength grades are available from the backend.'

          );

        }



        versionRef.current =

          version;



        gradeOptionsRef.current =

          gradeResponse;



        setGradeOptions(

          gradeResponse

        );



        const normalizedRows =

          sortStrengthRows(

            (

              strengthResponse ||

              []

            ).map(

              normalizeStrengthRow

            )

          );



        const statuses =

          {};



        const revisions =

          {};



        normalizedRows.forEach(

          (row) => {

            const key =

              getStrengthKey(

                row

              );



            statuses[key] =

              'saved';



            revisions[key] =

              0;

          }

        );



        rowsRef.current =

          normalizedRows;



        rowRevisionRef.current =

          revisions;



        setRows(

          normalizedRows

        );



        setRowStatuses(

          statuses

        );



        initializedRef.current = true;
        setInitialized(true);

      } catch (error) {

        if (cancelled) {

          return;

        }



        loadErrorRef.current = error?.message || 'Strength measurements could not be loaded.';
        setLoadError(loadErrorRef.current);

      } finally {

        if (!cancelled) {

          setLoading(false);

        }

      }

    }



    if (clinicalNoteId) {

      loadStrengthSection();

    } else {

      setLoadError(

        'Clinical note ID is required.'

      );



      setLoading(false);

    }



    return () => {

      cancelled = true;

    };

  }, [clinicalNoteId]);



  /**

   * Reload the current parent note version after a child save.

   */

  const refreshEntityVersion =

    useCallback(

      async () => {

        const noteResponse =

          await clinicalNoteService

            .getClinicalNote(

              clinicalNoteId

            );



        const newVersion =

          resolveEntityVersion(

            noteResponse

          );



        if (

          newVersion === null ||

          newVersion ===

            undefined

        ) {

          throw new Error(

            'Strength was saved, but the updated clinical note version could not be loaded.'

          );

        }



        versionRef.current =

          newVersion;



        if (

          typeof onVersionChange ===

          'function'

        ) {

          onVersionChange(

            newVersion,

            noteResponse

          );

        }



        return noteResponse;

      },

      [

        clinicalNoteId,

        onVersionChange,

      ]

    );



  /**

   * Mark one strength field as edited.

   */

  function updateRowField(

    key,

    field,

    value

  ) {

    if (disabled) {

      return;

    }



    const nextRows =

      rowsRef.current.map(

        (row) =>

          getStrengthKey(row) ===

          key

            ? {

                ...row,

                [field]: value,

              }

            : row

      );



    rowsRef.current =

      nextRows;



    setRows(

      nextRows

    );



    rowRevisionRef.current = {

      ...rowRevisionRef.current,



      [key]:

        (

          rowRevisionRef

            .current[key] ??

          0

        ) + 1,

    };



    /*

     * A new user edit is allowed to retry a row that previously

     * failed to autosave.

     */

    failedKeysRef.current.delete(

      key

    );



    unsavedKeysRef.current.add(key);
    dirtyKeysRef.current.add(

      key

    );



    setRowSaveErrors(

      (current) => ({

        ...current,

        [key]: '',

      })

    );



    const editedRow =

      nextRows.find(

        (row) =>

          getStrengthKey(

            row

          ) === key

      );



    const errors =

      editedRow

        ? validateStrengthRow(

            editedRow,

            gradeOptionsRef.current

          )

        : {};



    setRowStatuses(

      (current) => ({

        ...current,



        [key]:

          Object.keys(

            errors

          ).length > 0

            ? 'invalid'

            : 'unsaved',

      })

    );



    setEditTick(

      (current) =>

        current + 1

    );

  }



  /**

   * Merge a successful StrengthResponse into the current rows.

   */

  function applySuccessfulSave(

    key,

    response,

    revisionBeingSaved

  ) {

    const currentRevision =

      rowRevisionRef.current[

        key

      ] ?? 0;



    const preserveDraft =

      currentRevision !==

      revisionBeingSaved;const nextRows =

      rowsRef.current.map(

        (row) => {

          if (

            getStrengthKey(

              row

            ) !== key

          ) {

            return row;

          }



          return mergeSavedStrengthRow(

            row,

            response,

            preserveDraft

          );

        }

      );



    rowsRef.current =

      sortStrengthRows(

        nextRows

      );



    setRows(

      rowsRef.current

    );



    if (!preserveDraft) unsavedKeysRef.current.delete(key);

    if (preserveDraft) {

      dirtyKeysRef.current.add(

        key

      );



      setRowStatuses(

        (current) => ({

          ...current,

          [key]: 'unsaved',

        })

      );

    } else {

      setRowStatuses(

        (current) => ({

          ...current,

          [key]: 'saved',

        })

      );

    }

  }



  /**

   * Save dirty strength rows one at a time.

   *

   * Every successful strength save changes ClinicalNoteMain's

   * optimistic-lock version, so parallel saves would be unsafe.

   */

  const flushSaveQueue =

    useCallback(

      async () => {

        if (

          disabled ||

          !initialized ||

          savingRef.current

        ) {

          return;

        }



        savingRef.current =

          true;



        try {

          while (

            mountedRef.current &&

            dirtyKeysRef.current

              .size > 0

          ) {

            const key =

              Array.from(

                dirtyKeysRef.current

              ).find(

                (candidate) =>

                  !failedKeysRef

                    .current

                    .has(candidate)

              );



            if (!key) {

              break;

            }



            /*

             * Remove the active key while the request is in flight.

             *

             * If the clinician changes it again, updateRowField()

             * immediately places it back in the dirty set.

             */

            dirtyKeysRef.current.delete(

              key

            );



            const row =

              rowsRef.current.find(

                (candidate) =>

                  getStrengthKey(

                    candidate

                  ) === key

              );



            if (!row) {

              continue;

            }



            const errors =

              validateStrengthRow(

                row,

                gradeOptionsRef.current

              );



            if (

              Object.keys(

                errors

              ).length > 0

            ) {

              setRowStatuses(

                (current) => ({

                  ...current,

                  [key]:

                    'invalid',

                })

              );



              continue;

            }



            const revisionBeingSaved =

              rowRevisionRef.current[

                key

              ] ?? 0;



            setRowStatuses(

              (current) => ({

                ...current,

                [key]:

                  'saving',

              })

            );



            setRowSaveErrors(

              (current) => ({

                ...current,

                [key]: '',

              })

            );



            try {

              if (

                versionRef.current ===

                  null ||

                versionRef.current ===

                  undefined

              ) {

                await refreshEntityVersion();

              }



              const response =

                await clinicalNoteService

                  .saveStrength(

                    clinicalNoteId,

                    {

                      expectedVersion:

                        versionRef.current,



                      strengthTemplateId:

                        row.strengthTemplateId,



                      measurementSide:

                        row.measurementSide,



                      strengthGrade:

                        row.draftStrengthGrade ||

                        null,



                      comments:

                        row.draftComments

                          ?.trim() ||

                        null,

                    }

                  );



              if (

                !mountedRef.current

              ) {

                return;

              }



              /*

               * Saving this child record autosaves the parent note.

               * Obtain the new version before processing another row.

               */

              const noteResponse =

                await refreshEntityVersion();



              if (

                !mountedRef.current

              ) {

                return;

              }



              applySuccessfulSave(

                key,

                response,

                revisionBeingSaved

              );



              if (

                typeof onSaved ===

                'function'

              ) {

                onSaved(

                  response,

                  noteResponse

                );

              }

            } catch (error) {

              if (

                !mountedRef.current

              ) {

                return;

              }



              /*

               * Avoid an infinite retry loop.

               *

               * A failed row remains visibly failed until the user

               * changes it or presses Retry Save.

               */

              failedKeysRef.current.add(

                key

              );



              dirtyKeysRef.current.delete(

                key

              );



              setRowStatuses(

                (current) => ({

                  ...current,

                  [key]:

                    'error',

                })

              );



              setRowSaveErrors(

                (current) => ({

                  ...current,



                  [key]:

                    error?.message ||

                    'Strength measurement could not be autosaved.',

                })

              );

            }

          }

        } finally {

          savingRef.current =

            false;



          const hasRetryableDirtyRows =

            Array.from(

              dirtyKeysRef.current

            ).some(

              (key) =>

                !failedKeysRef

                  .current

                  .has(key)

            );



          if (

            mountedRef.current &&

            hasRetryableDirtyRows

          ) {

            window.setTimeout(

              () => {

                flushSaveRef

                  .current?.();

              },

              150

            );

          }

        }

      },

      [

        clinicalNoteId,

        disabled,

        initialized,

        onSaved,

        refreshEntityVersion,

      ]

    );



  // Share the in-flight autosave promise with visit navigation.
  useEffect(() => {
    flushSaveRef.current = () => {
      if (inFlightSaveRef.current) return inFlightSaveRef.current;
      const running = Promise.resolve().then(() => flushSaveQueue());
      const tracked = running.finally(() => {
        if (inFlightSaveRef.current === tracked) inFlightSaveRef.current = null;
      });
      inFlightSaveRef.current = tracked;
      return tracked;
    };
  }, [flushSaveQueue]);

  // Keep unsaved strength edits protected when switching visits.
  useEffect(() => {
    if (!navigationCoordinator || !clinicalNoteId) return undefined;
    return navigationCoordinator.register(clinicalNoteId, 'strength', {
      flush: async () => {
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
        if (dirtyKeysRef.current.size > 0) await flushSaveRef.current?.();
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
      },
      getState: () => {
        const unsaved = [...unsavedKeysRef.current];
        const invalid = unsaved.some((key) => {
          const row = rowsRef.current.find((item) => getStrengthKey(item) === key);
          return row && Object.keys(validateStrengthRow(row, gradeOptionsRef.current)).length > 0;
        });
        const failed = unsaved.some((key) => failedKeysRef.current.has(key));
        return {
          dirty: unsaved.length > 0 || !initializedRef.current,
          saving: Boolean(inFlightSaveRef.current),
          invalid,
          error: loadErrorRef.current ||
            (!initializedRef.current ? 'Strength is still loading.' : '') ||
            (failed ? 'A Strength measurement failed to save. Use Retry Save before leaving.' : ''),
        };
      },
    });
  }, [navigationCoordinator, clinicalNoteId]);



  /**

   * Debounced autosave trigger.

   */

  useEffect(() => {

    if (

      !initialized ||

      disabled ||

      dirtyKeysRef.current

        .size === 0

    ) {

      return undefined;

    }



    const timer =

      window.setTimeout(

        () => {

          flushSaveRef

            .current?.();

        },

        AUTOSAVE_DELAY_MS

      );



    return () => {

      window.clearTimeout(

        timer

      );

    };

  }, [

    editTick,

    initialized,

    disabled,

  ]);



  /**

   * Manually retry a failed strength save.

   */

  function retryRow(key) {

    if (disabled) {

      return;

    }



    failedKeysRef.current.delete(

      key

    );



    dirtyKeysRef.current.add(

      key

    );



    setRowSaveErrors(

      (current) => ({

        ...current,

        [key]: '',

      })

    );



    setRowStatuses(

      (current) => ({

        ...current,

        [key]: 'unsaved',

      })

    );



    setEditTick(

      (current) =>

        current + 1

    );

  }



  /**

   * Current validation state for all displayed rows.

   */

  const validationByKey =

    useMemo(() => {

      const result = {};



      rows.forEach(

        (row) => {

          result[

            getStrengthKey(

              row

            )

          ] =

            validateStrengthRow(

              row,

              gradeOptions

            );

        }

      );



      return result;

    }, [

      rows,

      gradeOptions,

    ]);



  /**

   * Overall autosave status.

   */

  const overallStatus =

    useMemo(() => {

      const values =

        Object.values(

          rowStatuses

        );



      if (

        values.includes(

          'saving'

        )

      ) {

        return 'Saving…';

      }



      if (

        values.includes(

          'error'

        )

      ) {

        return 'Autosave error';

      }



      if (

        values.includes(

          'invalid'

        )

      ) {

        return 'Needs correction';

      }



      if (

        values.includes(

          'unsaved'

        )

      ) {

        return 'Unsaved changes';

      }



      return disabled

        ? 'Read only'

        : 'Saved';

    }, [

      rowStatuses,

      disabled,

    ]);



  /**

   * Count unresolved backend strength warnings.

   */

  const unresolvedWarningCount =

    useMemo(

      () =>

        rows.filter(

          (row) =>

            (

              row.increaseWarning ||

              row.repetitionWarning

            ) &&

            !row.warningAcknowledged

        ).length,

      [rows]

    );



  /**

   * Show the exact clinician-facing scale received from Spring Boot.

   */

  const gradeScaleText =

    useMemo(

      () =>

        gradeOptions

          .map(

            (option) =>

              option.displayValue

          )

          .filter(Boolean)

          .join(', '),

      [gradeOptions]

    );



  if (loading) {

    return (

      <section

        style={{

          border:

            '1px solid #ddd',

          borderRadius: 10,

          padding: 16,

          background: '#fff',

        }}

      >

        Loading strength

        measurements…

      </section>

    );

  }if (loadError) {

    return (

      <section

        style={{

          border:

            '1px solid #c62828',

          borderRadius: 10,

          padding: 16,

          background:

            '#fff5f5',

        }}

      >

        <strong>

          Strength section could not

          be loaded

        </strong>



        <p

          style={{

            marginBottom: 0,

          }}

        >

          {loadError}

        </p>

      </section>

    );

  }



  return (

    <section

      style={{

        border:

          '1px solid #d7d7d7',

        borderRadius: 10,

        background: '#fff',

        overflow: 'hidden',

      }}

    >

      {/* =====================================================

          SECTION HEADER

          ===================================================== */}

      <div

        style={{

          display: 'flex',

          justifyContent:

            'space-between',

          alignItems: 'center',

          gap: 12,

          flexWrap: 'wrap',

          padding:

            '13px 16px',

          background:

            '#f7f8fa',

          borderBottom:

            '1px solid #ddd',

        }}

      >

        <div>

          <h3

            style={{

              margin: 0,

              fontSize: 17,

            }}

          >

            Gross Muscle Manual Test

          </h3>



          <div

            style={{

              marginTop: 3,

              fontSize: 12,

              color: '#666',

            }}

          >

            Strength measurements are

            compared with the most

            recent prior treatment

            date.

          </div>

        </div>



        <div

          style={{

            textAlign: 'right',

          }}

        >

          <div

            aria-live="polite"

            style={{

              fontSize: 13,

              fontWeight: 700,

            }}

          >

            {overallStatus}

          </div>



          {unresolvedWarningCount >

            0 && (

            <div

              style={{

                marginTop: 3,

                fontSize: 12,

                color: '#9a6700',

                fontWeight: 700,

              }}

            >

              {unresolvedWarningCount}{' '}

              unresolved strength{' '}

              {unresolvedWarningCount ===

              1

                ? 'warning'

                : 'warnings'}

            </div>

          )}

        </div>

      </div>



      <div

        style={{

          padding: 16,

        }}

      >

        {/* ===================================================

            BACKEND SCALE

            =================================================== */}

        {gradeScaleText && (

          <div

            style={{

              marginBottom: 14,

              border:

                '1px solid #d9e2ec',

              borderRadius: 8,

              background:

                '#f7fafc',

              padding: 11,

              fontSize: 13,

              lineHeight: 1.5,

            }}

          >

            <strong>

              Strength scale:

            </strong>{' '}

            {gradeScaleText}



            <br />



            Required grades may remain

            blank while the note is

            still in Draft, but final

            validation will require

            all mandatory strength

            measurements before

            review.

          </div>

        )}



        {/* ===================================================

            NO CONFIGURED FIELDS

            =================================================== */}

        {rows.length === 0 && (

          <div

            style={{

              border:

                '1px dashed #bbb',

              borderRadius: 8,

              padding: 16,

              color: '#666',

              background:

                '#fafafa',

            }}

          >

            No strength measurements

            are configured for this

            clinical note's body

            part.

          </div>

        )}



        {/* ===================================================

            STRENGTH ROWS

            =================================================== */}

        {rows.length > 0 && (

          <div

            style={{

              display: 'grid',

              gap: 12,

            }}

          >

            {rows.map(

              (row) => {

                const key =

                  getStrengthKey(

                    row

                  );



                return (

                  <StrengthMeasurementRow

                    key={key}

                    row={row}

                    gradeOptions={

                      gradeOptions

                    }

                    disabled={

                      disabled

                    }

                    status={

                      rowStatuses[

                        key

                      ] ||

                      'saved'

                    }

                    saveError={

                      rowSaveErrors[

                        key

                      ] ||

                      ''

                    }

                    validationError={

                      validationByKey[

                        key

                      ]

                        ?.strengthGrade ||

                      ''

                    }

                    onGradeChange={(

                      value

                    ) =>

                      updateRowField(

                        key,

                        'draftStrengthGrade',

                        value

                      )

                    }

                    onCommentsChange={(

                      value

                    ) =>

                      updateRowField(

                        key,

                        'draftComments',

                        value

                      )

                    }

                    onRetry={() =>

                      retryRow(

                        key

                      )

                    }

                  />

                );

              }

            )}

          </div>

        )}



        {/* ===================================================

            READ ONLY MESSAGE

            =================================================== */}

        {disabled && (

          <div

            style={{

              marginTop: 14,

              fontSize: 13,

              color: '#666',

            }}

          >

            Strength documentation is

            read only because this

            clinical note is no

            longer in the normal

            Draft editing state.

          </div>

        )}

      </div>

    </section>

  );

}